import { forwardRef, useImperativeHandle, useRef } from 'react'
import styles from '@shared/styles/components/TextInput.module.css'
import { VerticalContainer } from './Containers'
import designSystemStyles from '@shared/styles/designSystemStyles'
import { ErrorText, SmallText } from './Texts'

// Define custom ref type
export interface TextInputRef {
    focus: () => void
}

interface TextInputProps {
    label: string
    value: string
    onChange: (value: string) => void
    placeholder?: string
    type?: 'text' | 'password' | 'tel' | 'email' | 'number'
    inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal' | 'search' | 'url'
    error?: string
    loading?: boolean
    onEnterPress?: () => void
    onEnterFocus?: () => void
    onLostFocus?: () => void
    width?: string
    icon?: React.ReactNode
    className?: string
    disabled?: boolean
    /** Maximum input length, forwarded to the native input. */
    maxLength?: number
    /**
     * Native autocomplete hint. `"one-time-code"` is what makes iOS/Android surface
     * a code from an incoming message or email above the keyboard.
     */
    autoComplete?: string
    /** Native input name; some browser autofill heuristics key off it. */
    name?: string
}

export const TextInput = forwardRef<TextInputRef, TextInputProps>(({
    label, value, onChange, placeholder, error, loading = false, onEnterPress = ()=>{},
    width, icon, onEnterFocus = ()=>{}, onLostFocus = ()=>{}, type = 'text', inputMode, className, disabled = false,
    maxLength, autoComplete, name
}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus()
        }
    }))

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            if (onEnterPress) onEnterPress()
        }
    }

    return (
        <VerticalContainer 
            gap={designSystemStyles.gapXxxSm} 
            width={width || '100%'}
            className={['DSTextInput', styles['root-container'], className].filter(Boolean).join(' ')}
        >
            {label && (
                <SmallText text={label}/>
            )}
            <div className={styles['input-row']}>
                {icon && (
                    <div className={styles['input-icon']}>
                        {icon}
                    </div>
                )}
                <div
                    className={[
                        styles['text-input-wrapper'],
                        error ? styles['text-input-wrapper-error'] : '',
                        loading ? styles.skeleton : '',
                    ].filter(Boolean).join(' ')}
                >
                    <input
                        ref={inputRef}
                        className={[styles['input-container'], icon ? styles['input-with-icon'] : ''].filter(Boolean).join(' ')}
                        type={type}
                        inputMode={inputMode}
                        maxLength={maxLength}
                        autoComplete={autoComplete}
                        name={name}
                        placeholder={!loading && placeholder}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => onChange(e.target.value)}
                        onFocus={onEnterFocus}
                        onBlur={onLostFocus}
                        value={value}
                        disabled={loading || disabled}
                    />
                </div>
            </div>
            {error && 
                <ErrorText text={error}/>
            }
        </VerticalContainer>        
    )
})
// Add display name - THIS IS CRITICAL!
TextInput.displayName = 'TextInput'

export const TextAreaInput = forwardRef<TextInputRef, TextInputProps & { rows?: number }>(({
    label, value, onChange, placeholder, error, loading = false, onEnterPress = ()=>{},
    onEnterFocus = ()=>{}, onLostFocus = ()=>{}, width, className, disabled = false, rows = 3
}, ref) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle(ref, () => ({
        focus: () => {
            textAreaRef.current?.focus()
        }
    }))

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
         if (e.key === 'Enter' && !e.shiftKey && onEnterPress) {
            e.preventDefault()
            onEnterPress()
        }
    }

    return (
        <VerticalContainer
            gap={designSystemStyles.gapXxxSm}
            width={width || '100%'}
            className={['DSTextAreaInput', styles['root-container'], className].filter(Boolean).join(' ')}
        >
            {label && (
                <SmallText text={label}/>
            )}
            <div className={styles['input-row']}>
                <div
                    className={[
                        styles['text-input-wrapper'],
                        error ? styles['text-input-wrapper-error'] : '',
                        loading ? styles.skeleton : '',
                    ].filter(Boolean).join(' ')}
                >
                    <textarea
                        ref={textAreaRef}
                        className={styles['input-container']}
                        rows={rows}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={onEnterFocus}
                        onBlur={onLostFocus}
                        placeholder={!loading && placeholder}
                        disabled={loading || disabled}
                        style={{ resize: 'vertical' }}
                    />
                </div>
            </div>
            {error &&
                <ErrorText text={error}/>
            }
        </VerticalContainer>
    )
})

// Add display name - THIS IS CRITICAL!
TextAreaInput.displayName = 'TextAreaInput'
